/**
 * ============================================================================
 * FILTER CONFIG — properties mirror the CTS QuickSight WBR dashboard.
 * ============================================================================
 *
 * Source of truth: CollectionTrackerServiceCDK lib/quicksightStack.ts,
 * executive-summary-production sheet. QuickSight populates every dimension
 * dropdown FROM THE DATA (cascading, auto-populated); only Report Period and
 * Metric Group have static value lists. We follow the same rule:
 *
 * - Dimension VALUES come from useFilterOptions (src/features/api/
 *   filterOptions.ts) — distinct values from the cached getMetrics rowset.
 *   DO NOT hardcode dimension values here; a hardcoded value that doesn't
 *   exist in data silently filters everything to zero rows.
 * - STATIC_FILTER_OPTIONS holds only the QuickSight-static lists.
 *
 * QuickSight controls NOT yet supported:
 *   DCR Created Date Range.
 *
 * Customer Type and the Backup/BYOC/OTS/BYOT-Offline flags ARE row filters
 * now — the API sends customerType and the is*Dcr booleans, so the default
 * chips below constrain rows exactly as QuickSight's default state does.
 */

export const FILTER_PROPERTIES = [
  // Static-valued controls (QuickSight parity: SINGLE_SELECT static lists)
  {
    key: 'reportPeriod',
    operators: ['='],
    propertyLabel: 'Report Period',
    groupValuesLabel: 'Report Period values',
  },
  // Multi-select in the live dashboard; scopes the volume summary visuals only
  // (the OTD visuals have fixed rows). No 'OTD Metrics' value — the control
  // does not offer one.
  {
    key: 'metricGroup',
    operators: ['=', '!='],
    propertyLabel: 'Metric Group',
    groupValuesLabel: 'Metric Group values',
  },
  // QuickSight's "Metric Name" control — filters metric ROWS by catalog name,
  // values from the definitions catalog CASCADED by the Metric Group selection
  // (selectMetricNameOptions). Unlike the group scope it also filters
  // percentage rows: a name is an explicit pick.
  {
    key: 'metricName',
    operators: ['=', '!='],
    propertyLabel: 'Metric Name',
    groupValuesLabel: 'Metric Name values',
  },
  // QuickSight default-exclusion controls. These constrain rows client-side
  // (accessors in filterPredicate.ts) — the API sends customerType and the
  // is*Dcr flags, so the default state reproduces QuickSight's exclusions.
  {
    key: 'customerType',
    operators: ['=', '!='],
    propertyLabel: 'Customer Type',
    groupValuesLabel: 'Customer Type values',
  },
  {
    key: 'backupDcr',
    operators: ['='],
    propertyLabel: 'Backup DCR',
    groupValuesLabel: 'Backup DCR values',
  },
  {
    key: 'byocDcr',
    operators: ['='],
    propertyLabel: 'BYOC DCR',
    groupValuesLabel: 'BYOC DCR values',
  },
  {
    key: 'otsDcr',
    operators: ['='],
    propertyLabel: 'OTS DCR',
    groupValuesLabel: 'OTS DCR values',
  },
  {
    key: 'byotOfflineDcr',
    operators: ['='],
    propertyLabel: 'BYOT/Offline DCR',
    groupValuesLabel: 'BYOT/Offline DCR values',
  },
  // Data-driven dimension controls (values from the fetched rowset)
  {
    key: 'orderCategory',
    operators: ['=', '!='],
    propertyLabel: 'Order Category',
    groupValuesLabel: 'Order Category values',
  },
  {
    key: 'customer',
    operators: ['=', '!=', ':', '!:'],
    propertyLabel: 'Customer',
    groupValuesLabel: 'Customer values',
  },
  {
    key: 'locale',
    operators: ['=', '!='],
    propertyLabel: 'Locale',
    groupValuesLabel: 'Locale values',
  },
  {
    key: 'channel',
    operators: ['=', '!='],
    propertyLabel: 'Channel',
    groupValuesLabel: 'Channel values',
  },
  {
    key: 'subChannel',
    operators: ['=', '!='],
    propertyLabel: 'Sub Channel',
    groupValuesLabel: 'Sub Channel values',
  },
  {
    key: 'domain',
    operators: ['=', '!=', ':', '!:'],
    propertyLabel: 'Domain',
    groupValuesLabel: 'Domain values',
  },
  {
    key: 'dcrStatus',
    operators: ['=', '!='],
    propertyLabel: 'DCR Status',
    groupValuesLabel: 'DCR Status values',
  },
  {
    key: 'deliveryType',
    operators: ['=', '!='],
    propertyLabel: 'Delivery Type',
    groupValuesLabel: 'Delivery Type values',
  },
  {
    key: 'uom',
    operators: ['=', '!='],
    propertyLabel: 'UoM',
    groupValuesLabel: 'Unit of Measure values',
  },
  {
    key: 'dcrId',
    operators: ['=', '!=', ':', '!:'],
    propertyLabel: 'DCR ID',
    groupValuesLabel: 'DCR ID values',
  },
  // Backfill DCR: the accessor has always existed in filterPredicate, but with
  // no property here it could never be chipped. The live control defaults to
  // All, so this adds a control without changing any number.
  {
    key: 'backfillDcr',
    operators: ['='],
    propertyLabel: 'Backfill DCR',
    groupValuesLabel: 'Backfill DCR values',
  },
  // ──── Deep-dive-only controls (spec deep-dive-periodical R4.2-R4.4) ────
  // Demand Category is a FILTER on the deep dive; on the Detailed Summary it is
  // structural (one section group per checked category), hence both exist.
  {
    key: 'demandCategory',
    operators: ['=', '!='],
    propertyLabel: 'Demand Category',
    groupValuesLabel: 'Demand Category values',
  },
  {
    key: 'committedWeek',
    operators: ['=', '!='],
    propertyLabel: 'Committed Week',
    groupValuesLabel: 'Committed Week values',
  },
  {
    key: 'needByWeek',
    operators: ['=', '!='],
    propertyLabel: 'Need By Week',
    groupValuesLabel: 'Need By Week values',
  },
  // DCR Created Date Range (QuickSight dateTimePicker parity): a RANGE over
  // ISO calendar dates via >= / <= tokens — the two bounds AND together.
  // No dropdown values are offered (NO_OPTION_KEYS in filterOptions): a
  // distinct-date list would be hundreds of noise entries; the shell renders
  // date inputs instead.
  {
    key: 'dcrCreatedDate',
    operators: ['>=', '<='],
    propertyLabel: 'DCR Created Date',
    groupValuesLabel: 'DCR Created Date bounds',
  },
  // Period-level, not DCR-level: consumed by selectedPeriodFilter, never by
  // buildDcrPredicate (which takes a DcrRecord).
  {
    key: 'timePeriod',
    operators: ['='],
    propertyLabel: 'Time Period',
    groupValuesLabel: 'Time Period values',
  },
  {
    key: 'recentPeriodsOnly',
    operators: ['='],
    propertyLabel: 'Recent Periods Only',
    groupValuesLabel: 'Recent Periods Only values',
  },
  // Parameter-style control (not a row filter; consumed by OTD calc)
  {
    key: 'onTimeDeliveryThreshold',
    operators: ['=', '>=', '<='],
    propertyLabel: 'OTD Threshold %',
    groupValuesLabel: 'OTD Threshold values',
  },
  // Parameter-style control (not a row filter): the percentile for the
  // Launch TAT (TP) row in the DCR Health section. Default TP95.
  {
    key: 'launchTatPercentile',
    operators: ['='],
    propertyLabel: 'Percentile',
    groupValuesLabel: 'Percentile values',
  },
];

/**
 * Per-surface property scoping (QuickSight parity: each sheet carries its own
 * control set). The deep-dive sheet offers the DCR/period controls but NO
 * Metric Group / Metric Name / UoM / OTD-threshold; the summary sheets offer
 * those but not the deep-dive period/week controls. The FILTER STATE stays
 * global (deep-dive spec D3): a chip added on one surface still applies where
 * relevant and remains visible/removable everywhere — scoping only controls
 * which properties can be ADDED on a surface.
 */
const DEEP_DIVE_KEYS = new Set([
  'dcrCreatedDate',
  'reportPeriod',
  'recentPeriodsOnly',
  'timePeriod',
  'orderCategory',
  'customerType',
  'customer',
  'locale',
  'demandCategory',
  'domain',
  'channel',
  'subChannel',
  'dcrStatus',
  'deliveryType',
  'backupDcr',
  'byocDcr',
  'otsDcr',
  'backfillDcr',
  'byotOfflineDcr',
  'dcrId',
  'committedWeek',
  'needByWeek',
]);
/** Deep-dive-only controls — period/week grain, absent from the summary sheets. */
const DEEP_DIVE_ONLY_KEYS = new Set([
  'timePeriod',
  'recentPeriodsOnly',
  'committedWeek',
  'needByWeek',
  'demandCategory',
]);

export const DEEP_DIVE_FILTER_PROPERTIES = FILTER_PROPERTIES.filter((p) =>
  DEEP_DIVE_KEYS.has(p.key),
);
export const SUMMARY_FILTER_PROPERTIES = FILTER_PROPERTIES.filter(
  (p) => !DEEP_DIVE_ONLY_KEYS.has(p.key),
);

/**
 * The Executive Summary home page: its card set is fixed (the WBR preset),
 * so the metric-row filters are not offered there — they belong to the
 * pages that render the wider catalog. All other filters stay global.
 */
const HOME_EXCLUDED_KEYS = new Set(['metricGroup', 'metricName']);
export const HOME_FILTER_PROPERTIES = SUMMARY_FILTER_PROPERTIES.filter(
  (p) => !HOME_EXCLUDED_KEYS.has(p.key),
);

/**
 * Pinned PRIMARY controls per surface (spec filter-control-shell R2.1,
 * signed-off mock Option 5): the six DIMENSION filters people slice by are
 * the primaries on every surface; everything else — including the WBR parity
 * defaults (Report Period, Customer Type, Order Category, the DCR flags) —
 * lives behind "More filters (N)", whose count plus the "N filters differ
 * from WBR default" text carry the hidden-constraint signal. Deliberately
 * config-driven — re-pinning is a one-line edit here.
 */
const DIMENSION_PRIMARY_KEYS = [
  'customer',
  'domain',
  'locale',
  'channel',
  'subChannel',
  'dcrId',
];
export const PRIMARY_FILTER_KEYS = {
  summary: DIMENSION_PRIMARY_KEYS,
  deepDive: DIMENSION_PRIMARY_KEYS,
};

/**
 * ONLY the QuickSight-static lists. Everything else is data-driven — see
 * useFilterOptions.
 */
export const STATIC_FILTER_OPTIONS = [
  { propertyKey: 'reportPeriod', value: 'WBR' },
  { propertyKey: 'reportPeriod', value: 'MBR' },
  { propertyKey: 'reportPeriod', value: 'QBR' },
  { propertyKey: 'customerType', value: 'AGIDS' },
  { propertyKey: 'customerType', value: 'Internal' },
  { propertyKey: 'backupDcr', value: 'No' },
  { propertyKey: 'backupDcr', value: 'Yes' },
  { propertyKey: 'byocDcr', value: 'No' },
  { propertyKey: 'byocDcr', value: 'Yes' },
  { propertyKey: 'otsDcr', value: 'No' },
  { propertyKey: 'otsDcr', value: 'Yes' },
  { propertyKey: 'byotOfflineDcr', value: 'No' },
  { propertyKey: 'byotOfflineDcr', value: 'Yes' },
  { propertyKey: 'backfillDcr', value: 'No' },
  { propertyKey: 'backfillDcr', value: 'Yes' },
  // Deep-dive "Recent Periods Only" — static Yes/No, defaults to Yes.
  { propertyKey: 'recentPeriodsOnly', value: 'Yes' },
  { propertyKey: 'recentPeriodsOnly', value: 'No' },
  // Metric Group values are NOT here: they come from the definitions catalog
  // (selectMetricGroupOptions — every distinct definition.metricGroup), so a
  // new backend group appears automatically. "WBR Metrics Only" is a PRESET
  // (definition.presets), not a group — the WBR restriction is the home
  // page's hard scope, never a group value.
  { propertyKey: 'onTimeDeliveryThreshold', value: '90' },
  { propertyKey: 'onTimeDeliveryThreshold', value: '95' },
  { propertyKey: 'onTimeDeliveryThreshold', value: '99' },
  // Launch TAT percentile options (default TP95).
  { propertyKey: 'launchTatPercentile', value: '50' },
  { propertyKey: 'launchTatPercentile', value: '75' },
  { propertyKey: 'launchTatPercentile', value: '90' },
  { propertyKey: 'launchTatPercentile', value: '95' },
  { propertyKey: 'launchTatPercentile', value: '99' },
];

/**
 * @deprecated Dimension values are data-driven now (useFilterOptions).
 * Kept as an alias so stale imports fail loudly in review, not silently
 * at runtime with wrong values.
 */
export const FILTER_OPTIONS = STATIC_FILTER_OPTIONS;

/**
 * Default chips — full QuickSight default-state parity (from the live WBR
 * dashboard control bar): Report Period=WBR, Order Category=Production,
 * Customer Type=AGIDS, Backup/BYOC/OTS DCR=No, BYOT/Offline DCR=Yes+No.
 * Metric Group defaults to ALL (no chip): "WBR Metrics Only" is a PRESET, not
 * a group — the WBR restriction is the HOME PAGE's hard scope
 * (selectWbrDashboardData's preset parameter), never a default chip that
 * would restrict the other pages. When group chips ARE set they filter metric
 * ROWS of EVERY type, OTD included (QuickSight's "OTD visuals ignore the
 * control" was a platform limitation, deliberately not copied), and
 * metricGroupMatches still honours preset membership for persisted queries.
 */
export const DEFAULT_FILTER_QUERY = {
  tokens: [
    { propertyKey: 'reportPeriod', operator: '=', value: 'WBR' },
    { propertyKey: 'orderCategory', operator: '=', value: 'Production' },
    { propertyKey: 'customerType', operator: '=', value: 'AGIDS' },
    { propertyKey: 'backupDcr', operator: '=', value: 'No' },
    { propertyKey: 'byocDcr', operator: '=', value: 'No' },
    { propertyKey: 'otsDcr', operator: '=', value: 'No' },
    // BYOT/Offline is the ONE flag the live dashboard does NOT exclude: its
    // control ships with both values selected ("Yes, No" + Select all). Two
    // chips on the same property OR together, so this constrains nothing —
    // it mirrors the control's visible state. A single 'No' chip here dropped
    // every BYOT/Offline DCR and made all our totals lower than QuickSight's.
    { propertyKey: 'byotOfflineDcr', operator: '=', value: 'Yes' },
    { propertyKey: 'byotOfflineDcr', operator: '=', value: 'No' },
  ],
  operation: 'and' as const,
};

/**
 * How far the query differs from the parity defaults (`DEFAULT_FILTER_QUERY`) —
 * chips ADDED beyond the defaults PLUS default chips REMOVED. Used by the
 * zero-row empty states to decide whether to show a "Clear filters" action and
 * a count: the defaults are the baseline, not user filters, so a query equal to
 * the default reads as ZERO. "Clear filters" resets to the default (never to
 * empty — that would drop the parity chips), so offering it only when the query
 * genuinely differs keeps the button honest and avoids a no-op reset. Removals
 * count too: deleting a default chip changes filtering even though it's a subset
 * of the defaults.
 */
export function activeFilterCount(query: {
  tokens: Array<{ propertyKey: string; operator: string; value: string }>;
}): number {
  const same = (
    a: { propertyKey: string; operator: string; value: string },
    b: { propertyKey: string; operator: string; value: string },
  ) =>
    a.propertyKey === b.propertyKey &&
    a.operator === b.operator &&
    a.value === b.value;
  const added = query.tokens.filter(
    (t) => !DEFAULT_FILTER_QUERY.tokens.some((d) => same(d, t)),
  );
  const removed = DEFAULT_FILTER_QUERY.tokens.filter(
    (d) => !query.tokens.some((t) => same(t, d)),
  );
  // Time Period is one LOGICAL filter: editing the week selection writes one
  // token per checked week (unchecking 1 of 9 default weeks = 8 tokens), so
  // its tokens collapse to a single divergence rather than one per week.
  const addedTimePeriods = added.filter(
    (t) => t.propertyKey === 'timePeriod',
  ).length;
  return (
    added.length -
    addedTimePeriods +
    (addedTimePeriods > 0 ? 1 : 0) +
    removed.length
  );
}
